import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import { useAuthStore } from "../../stores/auth.store";
import * as SentryUtils from "../../utils/sentry";
import { formatShopPrice } from "./catalogue";
import {
  formatShopOrderDateTime,
  formatShopOrderTime,
  getShopOrderContentsLabel,
  getShopOrderLineTotalCents,
  getShopOrderStatus,
  getShopOrderTitle,
  type ShopOrderStatusTone,
} from "./orders";
import { fetchShopOrders, type ShopOrderWithPayment } from "./shop.service";
import {
  ActionButton,
  Container,
  ErrorMessage,
  StateMessage,
} from "./shop.styles";

export const SHOP_ORDERS_PATH = "/account/shop-orders";

const OrdersContainer = styled(Container)`
  max-width: 1284px;
  padding-top: 20px;

  @media (min-width: 768px) {
    padding-top: 28px;
  }
`;

const Title = styled.h1`
  color: #000;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  margin: 0 0 20px;
  text-transform: uppercase;
`;

const Intro = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0 0 28px;
  max-width: 744px;
  text-transform: uppercase;

  @media (min-width: 900px) {
    margin-bottom: 48px;
  }
`;

// Mobile shows either the list or the selected order. From 900px the list
// stays on the left and the detail sits beside it, as in the prototype.
const Layout = styled.div<{ $hasSelection: boolean }>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 32px;

  & > [data-pane="list"] {
    display: ${(props) => (props.$hasSelection ? "none" : "flex")};
  }

  & > [data-pane="detail"] {
    display: ${(props) => (props.$hasSelection ? "flex" : "none")};
  }

  @media (min-width: 900px) {
    flex-direction: row;
    gap: 96px;

    & > [data-pane="list"],
    & > [data-pane="detail"] {
      display: flex;
    }
  }
`;

const OrderList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 20px;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  @media (min-width: 900px) {
    flex: 0 0 348px;
    width: 348px;
  }
`;

// On desktop the detail sits beside the list, so the card being shown gets a
// light grey background to tie the two together. On mobile the list and the
// detail never share the screen and every card looks the same.
const OrderCard = styled(Link)<{ $isSelected: boolean }>`
  border: 1px solid #000;
  color: #000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  text-decoration: none;

  @media (min-width: 900px) {
    background: ${(props) =>
      props.$isSelected ? "rgba(0, 0, 0, 0.06)" : "transparent"};
    padding: 20px;

    &:hover {
      background: ${(props) =>
        props.$isSelected ? "rgba(0, 0, 0, 0.06)" : "rgba(0, 0, 0, 0.03)"};
    }
  }
`;

const OrderCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

const OrderCardTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
`;

const OrderCardDate = styled.span`
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
`;

const OrderCardSummary = styled.span`
  font-size: 14px;
  line-height: 1.35;
`;

const STATUS_COLORS: Record<
  ShopOrderStatusTone,
  { background: string; color: string }
> = {
  pending: { background: "#fff4e0", color: "#a45b00" },
  active: { background: "#e9f8ed", color: "#217a34" },
  cancelled: { background: "#f0f0f0", color: "#555" },
};

const StatusBadge = styled.span<{ $tone: ShopOrderStatusTone }>`
  align-self: flex-start;
  background: ${(props) => STATUS_COLORS[props.$tone].background};
  border: 1px solid ${(props) => STATUS_COLORS[props.$tone].color};
  color: ${(props) => STATUS_COLORS[props.$tone].color};
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
  text-transform: uppercase;
`;

const Detail = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;

  @media (min-width: 900px) {
    flex: 1 1 auto;
    max-width: 744px;
  }
`;

const DetailHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailSubtitle = styled.span`
  color: #000;
  font-size: 14px;
  line-height: 1.2;
`;

const DetailHeader = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 26px;

  @media (min-width: 900px) {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 32px;
  }
`;

const DetailTitle = styled.h2`
  color: #000;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  text-transform: uppercase;
`;

const Divider = styled.div<{ $isEdge: boolean }>`
  background: #000;
  height: ${(props) => (props.$isEdge ? "1px" : "0.5px")};
`;

const Line = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 16px 0;

  @media (min-width: 900px) {
    gap: 24px;
    padding: 20px 0;
  }
`;

const LineDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const LineName = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
`;

const LineVariant = styled.span`
  color: #000;
  font-size: 14px;
  line-height: 1.2;
  text-transform: uppercase;
`;

const LinePrices = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  text-align: right;
`;

const LineUnitPrice = styled.span`
  color: #000;
  font-size: 14px;
  line-height: 1.2;
`;

const LineTotal = styled.span`
  color: #000;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
`;

const TotalRow = styled.div`
  display: flex;
  font-size: 20px;
  font-weight: 700;
  justify-content: space-between;
  line-height: 1;
  padding: 16px 0 26px;

  @media (min-width: 900px) {
    padding: 20px 0 40px;
  }
`;

const InfoColumns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  @media (min-width: 900px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 96px;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 900px) {
    width: 348px;
  }
`;

const InfoTitle = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
`;

const InfoText = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const PaymentRow = styled.div`
  color: #000;
  display: flex;
  font-size: 14px;
  gap: 16px;
  justify-content: space-between;
  line-height: 1.2;

  & > span:last-child {
    overflow-wrap: anywhere;
    text-align: right;
  }
`;

const Notice = styled.div`
  border: 1px solid #000;
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 26px 0;
  padding: 15px;
  text-transform: uppercase;

  @media (min-width: 900px) {
    margin: 40px 0 26px;
    padding: 20px;
  }
`;

const CancelledNotice = styled(Notice)`
  margin: 0 0 26px;

  @media (min-width: 900px) {
    margin: 0 0 32px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: flex-start;
`;

const OrderLines = ({ order }: { order: ShopOrderWithPayment }) => {
  return (
    <>
      <Divider $isEdge />
      {order.shop_order_items.map((line, index) => {
        return (
          <div key={line.id}>
            {index > 0 && <Divider $isEdge={false} />}
            <Line>
              <LineDetails>
                <LineName>{line.product_name}</LineName>
                <LineVariant>{line.variant_label}</LineVariant>
              </LineDetails>
              <LinePrices>
                <LineUnitPrice>
                  {line.quantity} × {formatShopPrice(line.unit_price_cents)}
                </LineUnitPrice>
                <LineTotal>
                  {formatShopPrice(getShopOrderLineTotalCents(line))}
                </LineTotal>
              </LinePrices>
            </Line>
          </div>
        );
      })}
      <Divider $isEdge />
      <TotalRow>
        <span>TOTALE</span>
        <span>{formatShopPrice(order.total_cents)}</span>
      </TotalRow>
    </>
  );
};

export const ShopOrderDetail = ({ order }: { order: ShopOrderWithPayment }) => {
  const status = getShopOrderStatus(order.status);
  const isCancelled = order.status === "cancelled";
  const payment = order.payments;

  return (
    <Detail data-pane="detail" aria-label="Dettaglio ordine">
      <DetailHeader>
        <DetailHeading>
          <DetailTitle>{getShopOrderTitle(order)}</DetailTitle>
          <DetailSubtitle>
            ORE {formatShopOrderTime(order.created_at)} ·{" "}
            {getShopOrderContentsLabel(order)}
          </DetailSubtitle>
        </DetailHeading>
        <StatusBadge $tone={status.tone}>{status.label}</StatusBadge>
      </DetailHeader>

      {isCancelled && (
        <CancelledNotice>
          Questo ordine è stato annullato dal club. Per il rimborso o per
          chiarimenti scrivi agli amministratori.
        </CancelledNotice>
      )}

      <OrderLines order={order} />

      <InfoColumns>
        {order.pickup_note && (
          <InfoBlock>
            <InfoTitle>Note per il ritiro</InfoTitle>
            <InfoText>{order.pickup_note}</InfoText>
          </InfoBlock>
        )}
        {payment && (
          <InfoBlock>
            <InfoTitle>Pagamento</InfoTitle>
            <PaymentRow>
              <span>DATA</span>
              <span>{formatShopOrderDateTime(payment.created_at)}</span>
            </PaymentRow>
            {payment.amount_total !== null && (
              <PaymentRow>
                <span>IMPORTO</span>
                <span>{formatShopPrice(payment.amount_total)}</span>
              </PaymentRow>
            )}
          </InfoBlock>
        )}
      </InfoColumns>

      {!isCancelled && (
        <Notice>
          Non è prevista spedizione. Per il ritiro contattaci telefonicamente
          oppure presso il negozio DaiFradei di William e Fabio a Gavardo.
        </Notice>
      )}
    </Detail>
  );
};

type ShopOrdersViewProps = {
  orders: ShopOrderWithPayment[];
  selectedOrderId: string | null;
};

/**
 * The history and the selected order, from already loaded data. Kept apart
 * from the loading so it can be rendered on its own.
 */
export const ShopOrdersView = ({
  orders,
  selectedOrderId,
}: ShopOrdersViewProps) => {
  if (orders.length === 0) {
    return (
      <EmptyState>
        <StateMessage>Non hai ancora fatto ordini nello shop.</StateMessage>
        <ActionButton as={Link} to="/shop">
          Vai allo shop
        </ActionButton>
      </EmptyState>
    );
  }

  // Without a selection the wide layout still shows the newest order beside
  // the list; the narrow one shows the list alone.
  const shownOrder = selectedOrderId
    ? orders.find((order) => order.id === selectedOrderId)
    : orders[0];

  return (
    <Layout $hasSelection={selectedOrderId !== null}>
      <OrderList data-pane="list" aria-label="Ordini shop">
        {orders.map((order) => {
          const status = getShopOrderStatus(order.status);
          const isShown = order.id === shownOrder?.id;

          return (
            <li key={order.id}>
              <OrderCard
                to={`${SHOP_ORDERS_PATH}/${order.id}`}
                $isSelected={isShown}
                aria-current={isShown ? "page" : undefined}
              >
                <OrderCardHeader>
                  <OrderCardTitle>{getShopOrderTitle(order)}</OrderCardTitle>
                  <OrderCardDate>
                    ORE {formatShopOrderTime(order.created_at)}
                  </OrderCardDate>
                </OrderCardHeader>
                <OrderCardSummary>
                  {getShopOrderContentsLabel(order)} ·{" "}
                  {formatShopPrice(order.total_cents)}
                </OrderCardSummary>
                <StatusBadge $tone={status.tone}>{status.label}</StatusBadge>
              </OrderCard>
            </li>
          );
        })}
      </OrderList>

      {shownOrder ? (
        <ShopOrderDetail order={shownOrder} />
      ) : (
        <Detail data-pane="detail">
          <ErrorMessage>Ordine non trovato.</ErrorMessage>
        </Detail>
      )}
    </Layout>
  );
};

/**
 * `/account/shop-orders` and `/account/shop-orders/:orderId`: the family's
 * shop history with the detail of one order.
 */
export const ShopOrders = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const authUser = useAuthStore((state) => state.authUser);
  const [orders, setOrders] = useState<ShopOrderWithPayment[] | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    let isCurrent = true;

    setHasError(false);
    fetchShopOrders(authUser.id)
      .then((result) => {
        if (isCurrent) {
          setOrders(result);
        }
      })
      .catch((error) => {
        SentryUtils.captureException(error, {
          tags: { component: "ShopOrders", operation: "load_orders" },
        });

        if (isCurrent) {
          setHasError(true);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [authUser]);

  // Always the account page: on desktop the list stays beside the detail, so
  // going back to it would look like nothing happened.
  const handleBack = () => {
    navigate("/account");
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <ErrorMessage>
          Non è stato possibile caricare gli ordini. Riprova più tardi.
        </ErrorMessage>
      );
    }

    if (!orders) {
      return <StateMessage>Caricamento ordini...</StateMessage>;
    }

    return <ShopOrdersView orders={orders} selectedOrderId={orderId ?? null} />;
  };

  return (
    <OrdersContainer>
      <BackButton onClick={handleBack} />
      <Title>Ordini shop</Title>
      <Intro>
        Qui trovi tutti gli ordini fatti con l'account della tua famiglia. Gli
        ordini annullati restano visibili.
      </Intro>
      {renderContent()}
    </OrdersContainer>
  );
};
